import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { FloatingParticles } from './ParticleAnimation';
import { 
  Brain, 
  Cloud, 
  Smartphone, 
  Shield, 
  Database, 
  Palette,
  ArrowRight,
  Zap,
  Code,
  Globe,
  Cpu,
  Lock
} from 'lucide-react';

const iconMap = {
  'brain': Brain,
  'cloud': Cloud,
  'smartphone': Smartphone,
  'shield': Shield,
  'database': Database,
  'palette': Palette,
  'code': Code,
  'globe': Globe,
  'cpu': Cpu,
  'lock': Lock,
  'zap': Zap
};

// Services data - now focused on icons only
const services = [
  {
    id: 1,
    title: 'AI & Machine Learning',
    description: 'Advanced AI solutions that learn, adapt, and evolve with your business needs, powered by cutting-edge neural networks.',
    icon: 'brain',
    features: ['Natural Language Processing', 'Computer Vision', 'Predictive Analytics', 'Neural Networks'],
    is_featured: true,
    order: 1
  },
  {
    id: 2,
    title: 'Cloud Infrastructure',
    description: 'Scalable cloud solutions designed for performance, security, and reliability across multiple platforms.',
    icon: 'cloud',
    features: ['Multi-Cloud Strategy', 'DevOps Integration', 'Auto-Scaling', 'Disaster Recovery'],
    is_featured: true,
    order: 2
  },
  {
    id: 3,
    title: 'Mobile Development',
    description: 'Next-generation mobile apps that deliver exceptional user experiences across all devices.',
    icon: 'smartphone',
    features: ['Cross-Platform Apps', 'Native Performance', 'Real-time Features', 'App Store Optimization'],
    is_featured: true,
    order: 3
  },
  {
    id: 4,
    title: 'Cybersecurity',
    description: 'Comprehensive security solutions to protect your digital assets from evolving threats.',
    icon: 'shield',
    features: ['Threat Detection', 'Data Encryption', 'Security Audits', 'Compliance Management'],
    is_featured: false,
    order: 4
  },
  {
    id: 5,
    title: 'Data Analytics',
    description: 'Transform raw data into actionable insights for informed decision-making and strategic planning.',
    icon: 'database',
    features: ['Big Data Processing', 'Business Intelligence', 'Real-time Analytics', 'Data Visualization'],
    is_featured: false,
    order: 5
  },
  {
    id: 6,
    title: 'UI/UX Design',
    description: 'Beautiful, intuitive interfaces that users love to interact with, designed for maximum engagement.',
    icon: 'palette',
    features: ['User Research', 'Prototyping', 'Design Systems', 'Accessibility'],
    is_featured: false,
    order: 6
  }
];

export function ServicesSection() {
  const getColorClass = (index: number) => {
    const colors = ['primary', 'secondary', 'accent'];
    return colors[index % colors.length];
  };

  return (
    <section id="services" className="py-20 relative overflow-hidden">
      {/* Floating Particles */}
      <FloatingParticles 
        density={25}
        size="medium"
        colors={['#FFD700', '#00FFFF', '#00AEEF']}
        className="opacity-30"
      />
      
      <div className="absolute inset-0 circuit-pattern opacity-10"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-secondary/20 text-secondary border-secondary/30 font-orbitron">
            Our Services
          </Badge>
          <h2 className="text-4xl md:text-5xl font-orbitron font-bold text-primary mb-6">
            Comprehensive Tech Solutions
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-secondary to-primary mx-auto mb-8"></div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            From concept to deployment, we provide end-to-end technology services 
            that drive innovation and accelerate growth.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services
            .sort((a, b) => a.order - b.order)
            .map((service, index) => {
              const IconComponent = iconMap[service.icon as keyof typeof iconMap] || Zap;
              const isHighlighted = service.is_featured;
              const colorClass = getColorClass(index);
              
              return (
                <Card 
                  key={service.id} 
                  className={`group overflow-hidden bg-card/30 backdrop-blur border-${colorClass}/20 hover:border-${colorClass}/40 transition-all duration-300 hover:tech-glow hover:-translate-y-2 relative ${isHighlighted ? 'lg:col-span-1' : ''}`}
                >
                  {/* Enhanced particle overlay for featured services */}
                  {isHighlighted && (
                    <div className="absolute inset-0 opacity-20 pointer-events-none">
                      <FloatingParticles 
                        density={8}
                        size="small"
                        colors={[`var(--${colorClass})`]}
                      />
                    </div>
                  )}
                  
                  <div className="p-8 relative z-10">
                    {/* Enhanced Icon Design */}
                    <div className="relative mb-6 flex justify-center">
                      <div className={`w-20 h-20 bg-gradient-to-br from-${colorClass}/20 to-${colorClass}/5 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 relative overflow-hidden`}>
                        {/* Background glow effect */}
                        <div className={`absolute inset-0 bg-${colorClass}/10 rounded-2xl blur-xl group-hover:bg-${colorClass}/20 transition-colors duration-500`}></div>
                        
                        {/* Icon */}
                        <IconComponent className={`w-10 h-10 text-${colorClass} relative z-10 group-hover:drop-shadow-lg transition-all duration-300`} />
                        
                        {/* Animated border */}
                        <div className={`absolute inset-0 border-2 border-${colorClass}/30 rounded-2xl group-hover:border-${colorClass}/60 transition-colors duration-300`}></div>
                        
                        {/* Corner decorations */}
                        <div className={`absolute top-1 right-1 w-2 h-2 bg-${colorClass}/50 rounded-full animate-pulse`}></div>
                        <div className={`absolute bottom-1 left-1 w-1 h-1 bg-${colorClass}/30 rounded-full animate-pulse delay-300`}></div>
                      </div>
                      
                      {/* Outer glow ring */}
                      {/* <div className={`absolute inset-0 w-20 h-20 border border-${colorClass}/20 rounded-2xl animate-pulse group-hover:scale-125 transition-transform duration-700`}></div> */}
                    </div>
                    
                    <div className="text-center">
                      <h3 className={`text-xl font-orbitron font-semibold text-${colorClass} mb-4 group-hover:text-${colorClass} transition-colors duration-300`}>
                        {service.title}
                      </h3>
                      
                      <p className="text-muted-foreground mb-6 leading-relaxed">
                        {service.description}
                      </p>
                      
                      <div className="space-y-3 mb-8">
                        {service.features.slice(0, 4).map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center justify-center space-x-3">
                            <div className={`w-1.5 h-1.5 bg-${colorClass} rounded-full animate-pulse`} style={{ animationDelay: `${featureIndex * 200}ms` }}></div>
                            <span className="text-sm text-muted-foreground text-center">{feature}</span>
                          </div>
                        ))}
                      </div>
                      
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`w-full group-hover:bg-${colorClass}/10 group-hover:text-${colorClass} transition-all duration-300 relative overflow-hidden border border-${colorClass}/20 group-hover:border-${colorClass}/40`}
                      >
                        <span className="relative z-10 font-orbitron">Learn More</span>
                        <div className={`absolute inset-0 bg-gradient-to-r from-${colorClass}/10 to-transparent transform translate-x-full group-hover:translate-x-0 transition-transform duration-300`}></div>
                        <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Enhanced corner accent */}
                  <div className={`absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-${colorClass}/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                  
                  {/* Tech lines decoration */}
                  <div className={`absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-${colorClass}/30 to-transparent group-hover:via-${colorClass}/60 transition-colors duration-300`}></div>
                </Card>
              );
            })}
        </div>
        
        {/* Enhanced Call to Action */}
        <div className="text-center mt-16">
          <Card className="p-8 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 relative overflow-hidden">
            <FloatingParticles 
              density={10}
              size="small"
              colors={['#FFD700', '#00AEEF']}
              className="opacity-40"
            />
            
            <div className="relative z-10">
              <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6 tech-glow">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              
              <h3 className="text-2xl font-orbitron font-bold text-primary mb-4">
                Ready to Transform Your Business?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                Let's discuss how our cutting-edge solutions can accelerate your growth 
                and keep you ahead of the competition.
              </p>
              <Button 
                size="lg" 
                className="bg-primary text-primary-foreground hover:bg-primary/90 tech-glow font-orbitron relative overflow-hidden group"
              >
                <span className="relative z-10">Get Started Today</span>
                <div className="absolute inset-0 bg-gradient-to-r from-accent/20 to-secondary/20 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}